<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('coop_student_bans')"
                        :isNewButton="checkPermission('coopstudentban_store')"
                        @new-button-click="openCreateModal()"
                        :isFilter="true"
                        @filter-div-status="datatable.filterStatus = $event" />
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('coop_student_bans')"
                              :isNewButton="checkPermission('coopstudentban_store')"
                              @new-button-click="openCreateModal()"
                              :isFilter="true"
                              @filter-div-status="datatable.filterStatus = $event" />
            </template>

            <!-- Filter -->
            <datatable-filter v-show="datatable.filterStatus"
                               @filter="filter"
                               @filterClear="filterClear">
                <b-row>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('student_number')">
                            <b-form-input v-model="datatable.queryParams.filter['student.student_number']" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('coop_ban_type')">
                            <parameter-selectbox code="coop_student_ban.types" v-model="datatable.queryParams.filter.type" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <!-- Datatable -->
            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
            />

            <!-- Create Modal -->
            <CommonModal ref="createModal" size="md" :onHideOnlyX="true">
                <template v-slot:CommonModalTitle>
                    {{ $t('coop_new_student_ban') }}
                </template>
                <template v-slot:CommonModalContent>
                    <ValidationObserver ref="formModalValidate">
                        <b-form @submit.prevent="saveBan">
                            <ValidationProvider name="student_id" rules="required" v-slot="{valid, errors}">
                                <b-form-group :label="$t('student')">
                                    <students-auto-complete-by-key
                                        :value="selectedPresident"
                                        @user-input="handleSelection"
                                        :validate-error="errors[0]"></students-auto-complete-by-key>
                                </b-form-group>
                            </ValidationProvider>

                            <ValidationProvider name="type" rules="required" v-slot="{errors}">
                                <b-form-group :label="$t('coop_ban_type')">
                                    <parameter-selectbox code="coop_student_ban.types"
                                                         v-model="form.type"
                                                         :validate-error="errors[0]" />
                                </b-form-group>
                            </ValidationProvider>

                            <b-form-group :label="$t('coop_ban_reason')">
                                <b-form-textarea v-model="form.reason" rows="3" />
                            </b-form-group>

                            <div class="d-flex justify-content-end mt-3">
                                <b-button type="submit" variant="primary" :disabled="isSubmitting">
                                    {{ $t('save') }}
                                </b-button>
                            </div>
                        </b-form>
                    </ValidationObserver>
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>

<script>
    //Template
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header';
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import ParameterSelectbox from '@/components/interactive-fields/ParameterSelectbox';
    import StudentsAutoCompleteByKey from '@/components/interactive-fields/StudentsAutoCompleteByKey';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';

    //Service
    import CoopStudentBanService from '@/services/CoopStudentBanService';

    //Other
    import qs from 'qs';

    export default {
        name: 'CoopStudentBansIndex',
        components: {
            AppLayout,
            Header,
            HeaderMobile,
            DatatableFilter,
            Datatable,
            CommonModal,
            ParameterSelectbox,
            StudentsAutoCompleteByKey,
            ValidationObserver,
            ValidationProvider
        },
        metaInfo() {
            return {
                title: this.$t('coop_student_bans')
            };
        },
        data() {
            return {
                isSubmitting: false,
                selectedStudent: '',
                form: {
                    student_id: null,
                    type: null,
                    reason: ''
                },
                datatable: {
                    isLoading: false,
                    filterStatus: true,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('delete'),
                                    class: 'ri-delete-bin-line text-danger',
                                    permission: 'coopstudentban_delete',
                                    callback: (row) => {
                                        this.deleteBan(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('student_number'),
                            field: 'student_number',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('name'),
                            field: 'name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('surname'),
                            field: 'surname',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_ban_type'),
                            field: this.getLocaleField('type_name'),
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_ban_reason'),
                            field: 'reason',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_banned_by'),
                            field: 'banned_by_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('date'),
                            field: 'created_at',
                            formatFn: (value) => {
                                return value ? this.toLocaleDate(value) : ''
                            },
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: '-id',
                        page: 1,
                        limit: 20
                    }
                },
                selectedPresident: '',
            };
        },
        created() {
            this.getRows();
        },
        methods: {
            getRows() {
                if (!this.checkPermission('coopstudentban_index')) {
                    return;
                }
                this.datatable.isLoading = true;
                const config = {
                    params: { ...this.datatable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                CoopStudentBanService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination?.total || response.data.data.length;
                        this.datatable.showTable = true;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {};
                this.filter();
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.getRows();
            },
            onSortChange(params) {
                if (params && params.length > 0) {
                    this.datatable.queryParams.sort = (params[0].type === 'desc' ? '-' : '') + params[0].field;
                }
                this.getRows();
            },
            resetForm() {
                this.form = {
                    student_id: null,
                    type: null,
                    reason: ''
                };
                this.selectedStudent = '';
                this.selectedPresident = '';
            },
            openCreateModal() {
                this.resetForm();
                if (this.$refs.formModalValidate) {
                    this.$refs.formModalValidate.reset();
                }
                this.$refs.createModal.$refs.commonModal.show();
            },
            saveBan() {
                this.$refs.formModalValidate.validate().then((isValid) => {
                    if (!isValid) return;

                    this.isSubmitting = true;
                    CoopStudentBanService.store(this.form)
                        .then((response) => {
                            this.$toast.success(this.$t('api.' + response.data.message));
                            this.$refs.createModal.$refs.commonModal.hide();
                            this.getRows();
                        })
                        .catch((error) => {
                            this.showErrors(error, this.$refs.formModalValidate);
                        })
                        .finally(() => {
                            this.isSubmitting = false;
                        });
                });
            },
            deleteBan(row) {
                this.$swal.fire({
                    text: this.$t('coop_delete_student_ban_confirm'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopStudentBanService.remove(row.id)
                            .then((response) => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.getRows();
                            })
                            .catch((error) => {
                                this.showErrors(error, null);
                            });
                    }
                });
            },
            handleSelection(user) {
                this.form.student_id = user ? (user.id || user.student_id || user.user_id) : null;
                this.selectedPresident = user ? `${user.student_number || ''} ${user.name || ''} ${user.surname || ''}`.trim() : '';
            }
        }
    };
</script>
